import { useState } from 'react';
import axios from 'axios';

const API = '/api/super-admin';
const getToken = () => localStorage.getItem('gss_sa_token');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const STEPS = ['Creer le tenant', 'Reception kit', 'Import en masse', 'Activation'];

export default function SuperAdminOnboarding() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ nom: '', email: '', offre: 'Business' });
  const [tenantId, setTenantId] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Generate kit
  const generateKit = async () => {
    if (!form.nom) return setError('Nom societe requis');
    setError('');
    const url = `${API}/onboarding/kit?nom=${encodeURIComponent(form.nom)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `GSS360_KIT_ONBOARDING_${form.nom.toUpperCase().replace(/\s+/g, '_')}.xlsx`;
    a.click();
    setStep(1);
  };

  // Step 2: Preview
  const handlePreview = async () => {
    if (!file) return setError('Selectionnez le fichier Excel rempli');
    setError('');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/onboarding/preview`, fd, { headers: { ...headers(), 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur validation');
    } finally { setLoading(false); }
  };

  // Step 3: Import
  const handleImport = async () => {
    if (!file) return;
    setError('');
    setLoading(true);
    try {
      // Create tenant first if needed
      let tid = tenantId;
      if (!tid) {
        const res = await axios.post(`${API}/tenants`, { nom: form.nom, email_admin: form.email, offre: form.offre }, { headers: headers() });
        tid = res.data.id || res.data.tenant_id;
        setTenantId(tid);
      }

      const fd = new FormData();
      fd.append('file', file);
      fd.append('tenant_id', tid);
      const res = await axios.post(`${API}/onboarding/import`, fd, { headers: { ...headers(), 'Content-Type': 'multipart/form-data' } });
      setRapport(res.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur import');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#8B0000', marginBottom: 8 }}>Kit d'Onboarding</h1>
      <p style={{ fontSize: 13, color: '#666', marginBottom: 24 }}>Processus d'integration d'un nouveau client GSS-360</p>

      {/* Stepper */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 32 }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', margin: '0 auto 6px',
              background: i <= step ? '#8B0000' : '#e0e0e0',
              color: i <= step ? '#fff' : '#999',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700,
            }}>{i + 1}</div>
            <div style={{ fontSize: 11, color: i <= step ? '#8B0000' : '#999', fontWeight: i === step ? 700 : 400 }}>{s}</div>
          </div>
        ))}
      </div>

      {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 12, color: '#DC2626' }}>{error}</div>}

      {/* Step 0: Create */}
      {step === 0 && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Etape 1 — Informations societe</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Nom societe *</label>
              <input className="gss-input" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Raison sociale du client" style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Email admin</label>
              <input className="gss-input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="admin@societe.com" style={{ width: '100%' }} />
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Offre</label>
            <select className="gss-input" value={form.offre} onChange={e => setForm(f => ({ ...f, offre: e.target.value }))} style={{ width: 200 }}>
              <option>Starter</option>
              <option>Business</option>
              <option>Enterprise</option>
            </select>
          </div>
          <button onClick={generateKit} style={{ height: 36, fontSize: 13, padding: '0 24px', background: '#8B0000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            Generer kit onboarding (Excel)
          </button>
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 1 && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Etape 2 — Recevoir le kit rempli</h2>
          <p style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>Uploadez le fichier Excel rempli par le client pour validation.</p>
          <input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])} style={{ marginBottom: 16 }} />
          <br />
          <button onClick={handlePreview} disabled={loading} style={{ height: 36, fontSize: 13, padding: '0 24px', background: '#8B0000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, opacity: loading ? 0.5 : 1 }}>
            {loading ? 'Validation...' : 'Valider et previsualiser'}
          </button>
        </div>
      )}

      {/* Step 2: Preview + Import */}
      {step === 2 && preview && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Etape 3 — Apercu et import</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Agents', val: preview.agents, color: '#2E7D32' },
              { label: 'Sites', val: preview.sites, color: '#1565C0' },
              { label: 'Clients', val: preview.clients, color: '#F57F17' },
              { label: 'Vehicules', val: preview.vehicules, color: '#6A1B9A' },
              { label: 'Utilisateurs', val: preview.utilisateurs, color: '#E65100' },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: 'center', padding: 12, background: '#f8f8f8', borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.val}</div>
                <div style={{ fontSize: 11, color: '#666' }}>{item.label}</div>
              </div>
            ))}
          </div>
          {preview.erreurs.length > 0 && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#DC2626', marginBottom: 6 }}>{preview.erreurs.length} avertissement(s)</div>
              {preview.erreurs.map((e, i) => <div key={i} style={{ fontSize: 11, color: '#991B1B' }}>{e}</div>)}
            </div>
          )}
          <button onClick={handleImport} disabled={loading} style={{ height: 36, fontSize: 13, padding: '0 24px', background: '#8B0000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, opacity: loading ? 0.5 : 1 }}>
            {loading ? 'Import en cours...' : 'Importer toutes les donnees'}
          </button>
        </div>
      )}

      {/* Step 3: Rapport */}
      {step === 3 && rapport && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#2E7D32', marginBottom: 16 }}>Etape 4 — Import termine</h2>
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 14, fontWeight: 600, color: '#16A34A' }}>
            {rapport.total_importes} elements importes avec succes
            {rapport.total_erreurs > 0 && <span style={{ color: '#DC2626' }}> — {rapport.total_erreurs} erreur(s)</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 20 }}>
            {['agents', 'sites', 'clients', 'vehicules', 'utilisateurs'].map(k => (
              <div key={k} style={{ textAlign: 'center', padding: 10, background: '#f8f8f8', borderRadius: 8 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: rapport.rapport[k].erreurs.length > 0 ? '#DC2626' : '#16A34A' }}>
                  {rapport.rapport[k].importes}
                </div>
                <div style={{ fontSize: 11, color: '#666', textTransform: 'capitalize' }}>{k}</div>
                {rapport.rapport[k].erreurs.length > 0 && (
                  <div style={{ fontSize: 9, color: '#DC2626' }}>{rapport.rapport[k].erreurs.length} err.</div>
                )}
              </div>
            ))}
          </div>
          {Object.entries(rapport.rapport).some(([, v]) => v.erreurs.length > 0) && (
            <details style={{ marginBottom: 16 }}>
              <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#DC2626' }}>Voir les erreurs</summary>
              <div style={{ marginTop: 8, fontSize: 11, color: '#991B1B' }}>
                {Object.entries(rapport.rapport).map(([k, v]) =>
                  v.erreurs.map((e, i) => <div key={`${k}-${i}`}>{e}</div>)
                )}
              </div>
            </details>
          )}
          <button onClick={() => { setStep(0); setPreview(null); setRapport(null); setFile(null); setForm({ nom: '', email: '', offre: 'Business' }); }} style={{ height: 36, fontSize: 13, padding: '0 24px', background: '#8B0000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            Nouvel onboarding
          </button>
        </div>
      )}
    </div>
  );
}
